import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useCallback, useState } from 'react';
import { DocumentTitle } from 'components/DocumentTitle';
import { useTrack } from 'utils/hooks/useMixPanel';
import { useNavigation } from 'utils/hooks/useNavigation';
import { RESCHEDULE_MAX_TIME_GAP } from 'domains/direct-reschedule/useRescheduleProvider';
import { ReschedulePendingRescheduleDto, reschedulePendingReschedule } from 'domains/direct-reschedule/queries';
import { ApiError } from 'domains/api/errors';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { RescheduleConfirmationDialog, RescheduleConfirmationResponse } from './RescheduleConfirmationDialog';
import {
  Button,
  FormLayout,
  Heading,
  LoadingSpinner,
  PageContainer,
  Paragraph,
  Reservation,
  ReservationCard,
  useDialog,
  useToast,
} from '@expressable/syntax-ds';
import { DATE_LONG_DAY_MONTH_YEAR, TIME_SHORT } from 'utils/date';
import { useFormContext, useWatch } from 'react-hook-form';
import { RescheduleDirectFormData } from '../schema';
import { Reschedule } from 'domains/direct-reschedule/types';
import { differenceInHours } from 'date-fns';
import { RESCHEDULE_DIRECT_MIXPANEL_EVENTS } from '../utils/mixpanel';

type RescheduleConfirmationProps = {
  selectedTherapist?: string;
  rescheduleInformation: Reschedule;
  onEditReservation: () => void;
};

const isTimeSlotAvailable = (timeSlot: Date): boolean => {
  return differenceInHours(timeSlot, new Date()) >= RESCHEDULE_MAX_TIME_GAP;
};

const RescheduleConfirmation = ({ selectedTherapist, rescheduleInformation, onEditReservation }: RescheduleConfirmationProps) => {
  const { track } = useTrack(RESCHEDULE_DIRECT_MIXPANEL_EVENTS.ViewedRescheduleDirectConfirmation);
  const { dialogStateProps: RescheduleDirectConfirmDialogProps, onOpen: onOpenRescheduleDirectConfirmDialog } =
    useDialog();
  const { clientId, rescheduleId } = useParams<{ clientId: string; rescheduleId: string }>();
  const queryClient = useQueryClient();
  const { control } = useFormContext<RescheduleDirectFormData>();
  const selectedCalendarId = useWatch({ control, name: 'calendarId' });
  const timeSlot = useWatch({ control, name: 'timeSlot' });

  const { format, displayTimezone } = useDisplayTimezone();
  const { routes, navigate: sendTo } = useNavigation();

  const [modalStatus, setModalStatus] = useState<RescheduleConfirmationResponse | null>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { mutateAsync: rescheduleAppointment, isLoading: isRescheduling } = useMutation<
    unknown,
    ApiError,
    ReschedulePendingRescheduleDto
  >(
    data => {
      if (!clientId || !rescheduleId) throw new Error('clientId or rescheduleId is undefined');
      const response = reschedulePendingReschedule(clientId, rescheduleId, data);

      return response;
    },
    {
      onSuccess: async () => {
        track(RESCHEDULE_DIRECT_MIXPANEL_EVENTS.ConfirmedRescheduleDirect);

        // Only track if a different therapist was actually selected
        // Compare as numbers to handle any type coercion issues
        if (
          selectedCalendarId && 
          rescheduleInformation?.originalAcuityAppointment?.calendarID &&
          parseInt(selectedCalendarId, 10) !== parseInt(rescheduleInformation.originalAcuityAppointment.calendarID, 10)
        ) {
          track(RESCHEDULE_DIRECT_MIXPANEL_EVENTS.RescheduledDifferentTherapist);
        }

        setModalStatus('success');
        onOpenRescheduleDirectConfirmDialog();
      },
      onError: async error => {
        // @ts-expect-error @TODO proper types here
        const code = error?.originalError?.response?.data?.code;

        switch (code) {
          case 'PENDING_RESCHEDULE_NOT_ACTIVE':
          case 'ACUITY_CALENDAR_NOT_AVAILABILITY':
            await queryClient.invalidateQueries('therapists-availability');
            setModalStatus('inactive');
            break;
          case 'PENDING_RESCHEDULE_NOT_FOUND':
            setModalStatus('notfound');
            break;
          default:
            // dev-note(gian): this is a catch-all error message for any other error that we don't know about
            showToast({
              variant: 'error',
              message: t('reschedule.confirm.genericError'),
              duration: 8000,
            });
            sendTo(routes.root);
            return;
        }
        onOpenRescheduleDirectConfirmDialog();
      },
    },
  );

  const onConfirm = useCallback(() => {
    if (modalStatus !== 'success') {
      navigate(-1);
    } else {
      // Show success toast and redirect to home page
      showToast({
        variant: 'success',
        message: t('reschedule.confirm.rescheduleSuccessTitle'),
        duration: 8000,
      });
      sendTo(routes.root);
    }
  }, [modalStatus, showToast, t, sendTo, routes.root, navigate]);

  if (!timeSlot || !displayTimezone) {
    showToast({
      variant: 'error',
      message: t('reschedule.confirm.invalidRescheduleState'),
      duration: 8000,
    });
    sendTo(`/reschedule/${clientId}/${rescheduleId}`);
    return null;
  }

  const onSubmit = () => {
    // edge-case: if the user has been on the page for more than 2 hours from the selected timeslot, we don't allow them to reschedule
    if (!isTimeSlotAvailable(timeSlot)) {
      setModalStatus('inactive');
      onOpenRescheduleDirectConfirmDialog();

      return;
    }

    const payload: ReschedulePendingRescheduleDto = {
      dateTime: timeSlot.toISOString(),
      timeZone: displayTimezone?.resolvedName ?? '',
    };

    // Only include calendarID if a different therapist was selected (compare as numbers)
    if (
      selectedCalendarId && 
      rescheduleInformation?.originalAcuityAppointment?.calendarID &&
      parseInt(selectedCalendarId, 10) !== parseInt(rescheduleInformation.originalAcuityAppointment.calendarID, 10)
    ) {
      payload.calendarID = parseInt(selectedCalendarId, 10);
    }

    rescheduleAppointment(payload);
  };

  return (
    <>
      <DocumentTitle>{t('reschedule.pageTitle')}</DocumentTitle>
      <PageContainer variant="form">
        <FormLayout>
          <FormLayout.Fields>
            <div>
              <Heading level="h1">
                {t('reschedule.confirm.mainHeading')}
              </Heading>

              <Paragraph noDefaultMargins size="lg" variant="primary">
                {t('reschedule.confirm.introText')}
              </Paragraph>
            </div>

            <ReservationCard title={t('reschedule.confirm.card.title')}>
              <Reservation
                ctaLabel={t('reschedule.confirm.card.cta')}
                dateLabel={`${format(timeSlot, DATE_LONG_DAY_MONTH_YEAR)} ${
                  selectedTherapist
                    ? `with ${selectedTherapist}`
                    : rescheduleInformation?.therapistFirstName && rescheduleInformation?.therapistLastName
                    ? `with ${rescheduleInformation.therapistFirstName} ${rescheduleInformation.therapistLastName}`
                    : ''
                }`}
                onCtaClick={onEditReservation}
                timeLabel={`${format(timeSlot, TIME_SHORT)} ${displayTimezone.label}`}
                title={`${rescheduleInformation?.originalAcuityAppointment?.type} (${rescheduleInformation?.originalAcuityAppointment?.duration}min)`}
              />
            </ReservationCard>
          </FormLayout.Fields>
        </FormLayout>

        <FormLayout.CTA>
          <Button onClick={onSubmit} size="lg" disabled={isRescheduling} variant="primary">
            {t('reschedule.confirm.submit')}
            {isRescheduling && <LoadingSpinner />}
          </Button>
        </FormLayout.CTA>

        <RescheduleConfirmationDialog
          {...RescheduleDirectConfirmDialogProps}
          status={modalStatus}
          onConfirm={onConfirm}
          isDismissible={false}
          isLoading={isRescheduling}
        />
      </PageContainer>
    </>
  );
};

export default RescheduleConfirmation;
